import React from "react";
import { Modal, Steps, Typography, Button, Divider } from "antd";
import {
  FileTextOutlined,
  TeamOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useImpartirTerapiaWizardController } from "./useImpartirTerapiaWizardController";
import { StepSelectTherapy, StepSelectPatients, StepReview } from "./steps";
import "./ImpartirTerapiaWizardModal.scss";
import { useMovil } from "../../../../../hooks/useIsNarrow";
import { useHistorialHoja } from "../../../../../hooks/useHistorialHoja";

const { Title, Text } = Typography;

export function ImpartirTerapiaWizardModal({ open, onCancel, onCreated }) {
  const movil = useMovil();

  // El boton atras del telefono cierra el asistente en vez de sacar de la pagina.
  useHistorialHoja(movil && open, onCancel);

  // "Cancelar" y "Siguiente" salen de common: son acciones de todo el portal.
  const { t } = useTranslation(["therapy", "common"]);

  const {
    step,
    setStep,
    canNext,
    loadingPrimary,
    creating,
    reset,
    confirmCreateSession,

    // step 1
    therapies,
    therapySearch,
    setTherapySearch,
    selectedTherapyId,
    setSelectedTherapyId,

    // step 2
    patients,
    patientSearch,
    setPatientSearch,
    sexo,
    setSexo,
    selectedPatientIds,
    togglePatient,
    toggleManyPatients,

    // step 3
    therapyDetail,
    loadingDetail,
  } = useImpartirTerapiaWizardController({ open, onCreated });

  const close = () => {
    reset();
    onCancel?.();
  };

  const steps = [
    { title: t("wizard.pasos.terapia"), icon: <FileTextOutlined /> },
    { title: t("wizard.pasos.pacientes"), icon: <TeamOutlined /> },
    { title: t("wizard.pasos.validacion"), icon: <CheckCircleOutlined /> },
  ];

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      width={980}
      destroyOnHidden
      className="revi-impartir-wizard"
      rootClassName="revi-hoja"
      title={
        <div className="revi-impartir-wizard__title">
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {t("wizard.titulo")}
            </Title>
            <Text type="secondary">{t("wizard.subtitulo")}</Text>
          </div>
        </div>
      }
    >
      <Steps
        current={step}
        items={steps}
        className="revi-impartir-wizard__steps"
      />

      <Divider style={{ margin: "14px 0" }} />

      <div className="revi-impartir-wizard__body">
        {step === 0 && (
          <StepSelectTherapy
            loading={loadingPrimary}
            therapies={therapies}
            search={therapySearch}
            onSearch={setTherapySearch}
            selectedTherapyId={selectedTherapyId}
            onSelect={setSelectedTherapyId}
          />
        )}

        {step === 1 && (
          <StepSelectPatients
            loading={loadingPrimary}
            patients={patients}
            search={patientSearch}
            onSearch={setPatientSearch}
            sexo={sexo}
            onSexo={setSexo}
            selectedPatientIds={selectedPatientIds}
            onTogglePatient={togglePatient}
            onToggleManyPatients={toggleManyPatients}
          />
        )}

        {step === 2 && (
          <StepReview
            loading={loadingDetail}
            therapyDetail={therapyDetail}
            selectedPatientIds={selectedPatientIds}
            patients={patients}
          />
        )}
      </div>

      <Divider style={{ margin: "14px 0" }} />

      <div className="revi-impartir-wizard__footer">
        <div>
          {step > 0 ? (
            <Button onClick={() => setStep((s) => Math.max(0, s - 1))}>
              {t("wizard.atras")}
            </Button>
          ) : null}
        </div>

        <div className="revi-impartir-wizard__footerRight">
          <Button onClick={close}>{t("common:actions.cancel")}</Button>

          {step < 2 ? (
            <Button
              type="primary"
              onClick={() => setStep((s) => Math.min(2, s + 1))}
              disabled={!canNext || loadingPrimary}
            >
              {t("common:actions.next")}
            </Button>
          ) : (
            // `loading` no es cosmetico: mientras el servidor crea la sesion el
            // boton no acepta un segundo toque, que hoy creaba otra sesion y
            // otra sala VR.
            <Button
              type="primary"
              onClick={confirmCreateSession}
              loading={creating}
              disabled={
                creating || !therapyDetail || selectedPatientIds.length === 0
              }
            >
              {t("wizard.iniciar")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
